import { AccountsUnsupportedError } from '@wiztime/sync';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/Button';
import styles from './Sync.module.css';
import {
  accountFailureKey,
  isRetryableFailure,
  type AccountFailureKey,
  type SyncFailureKey,
} from './syncErrors';

type FailureKey = AccountFailureKey | SyncFailureKey;

/** The shortest login password accepted (docs/architecture-account.md section 4). */
export const MIN_PASSWORD_LENGTH = 10;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

/** A plain format check; the server decides whether the address is usable. */
export function isValidEmail(text: string): boolean {
  return EMAIL_PATTERN.test(text.trim());
}

/** The first problem of a new password and its confirmation, or null when both are fine. */
export const PasswordProblem = { Short: 'short', Mismatch: 'mismatch' } as const;
export type PasswordProblem = (typeof PasswordProblem)[keyof typeof PasswordProblem];

export function passwordProblem(password: string, confirm: string): PasswordProblem | null {
  if (password.length < MIN_PASSWORD_LENGTH) return PasswordProblem.Short;
  if (password !== confirm) return PasswordProblem.Mismatch;
  return null;
}

interface FieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly testId: string;
  /** Message under the field; shown as an error when `invalid`. */
  readonly message?: string;
  readonly invalid?: boolean;
  readonly autoComplete?: string;
}

/** A labelled text input for the email address. */
export function EmailField({ label, value, onChange, testId, message, invalid, autoComplete }: FieldProps) {
  const id = useId();
  return (
    <div className={styles.field}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="email"
        value={value}
        autoComplete={autoComplete ?? 'username'}
        autoCapitalize="none"
        spellCheck={false}
        aria-invalid={invalid === true}
        aria-describedby={message === undefined ? undefined : `${id}-message`}
        data-testid={testId}
        onChange={(event) => onChange(event.target.value)}
      />
      {message === undefined ? null : (
        <p id={`${id}-message`} className={invalid === true ? styles.errorText : styles.muted}>
          {message}
        </p>
      )}
    </div>
  );
}

/** A labelled password input with a show/hide toggle. */
export function PasswordField({
  label,
  value,
  onChange,
  testId,
  message,
  invalid,
  autoComplete,
}: FieldProps) {
  const { t } = useTranslation();
  const id = useId();
  const [shown, setShown] = useState(false);
  return (
    <div className={styles.field}>
      <label htmlFor={id}>{label}</label>
      <div className={styles.fieldRow}>
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          value={value}
          autoComplete={autoComplete ?? 'current-password'}
          spellCheck={false}
          aria-invalid={invalid === true}
          aria-describedby={message === undefined ? undefined : `${id}-message`}
          data-testid={testId}
          onChange={(event) => onChange(event.target.value)}
        />
        <Button
          aria-pressed={shown}
          onClick={() => setShown((current) => !current)}
          data-testid={`${testId}-toggle`}
        >
          {shown ? t('sync.account.field.hide') : t('sync.account.field.show')}
        </Button>
      </div>
      {message === undefined ? null : (
        <p id={`${id}-message`} className={invalid === true ? styles.errorText : styles.muted}>
          {message}
        </p>
      )}
    </div>
  );
}

/** Progress shown while the password is turned into keys (Argon2, a few seconds). */
export function AccountProgress() {
  const { t } = useTranslation();
  return (
    <div className={styles.progress} role="status" data-testid="account-progress">
      <progress aria-label={t('sync.account.progress')} />
      <span>{t('sync.account.progress')}</span>
    </div>
  );
}

interface SubmitState {
  readonly busy: boolean;
  /** Translation key of the message under the form; null when nothing failed. */
  readonly failure: FailureKey | null;
  readonly retryable: boolean;
  /** Runs one account request; resolves with its result, or `undefined` when it failed (the message is set). */
  readonly run: <T>(task: () => Promise<T>) => Promise<T | undefined>;
  readonly clearFailure: () => void;
}

/**
 * The loading and error states shared by all account forms. While a request runs the form is busy;
 * a failure leaves the inputs as they are and sets a readable message. A server without account
 * endpoints is reported to `onUnsupported` so the home view can hide the account area.
 */
export function useAccountSubmit(
  onUnsupported?: () => void,
  failureKeyOf: (error: unknown) => FailureKey = accountFailureKey,
): SubmitState {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<FailureKey | null>(null);
  const [retryable, setRetryable] = useState(false);

  const run = async <T,>(task: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setFailure(null);
    try {
      return await task();
    } catch (error) {
      console.warn('[sync] account request failed', error);
      const key = failureKeyOf(error);
      setFailure(key);
      setRetryable(isRetryableFailure(key));
      if (error instanceof AccountsUnsupportedError) onUnsupported?.();
      return undefined;
    } finally {
      setBusy(false);
    }
  };
  return { busy, failure, retryable, run, clearFailure: () => setFailure(null) };
}

interface FailureProps {
  readonly failure: FailureKey | null;
  readonly retryable: boolean;
  readonly onRetry: () => void;
}

/** The error message under a form; network and server errors offer "Retry". */
export function AccountFailure({ failure, retryable, onRetry }: FailureProps) {
  const { t } = useTranslation();
  if (failure === null) return null;
  return (
    <div className={styles.failureRow}>
      <p className={styles.errorText} role="alert" data-testid="account-failure" data-key={failure}>
        {t(failure)}
      </p>
      {retryable ? (
        <Button onClick={onRetry} data-testid="account-retry">
          {t('sync.account.retry')}
        </Button>
      ) : null}
    </div>
  );
}

interface FormProps {
  readonly title: string;
  readonly intro?: string;
  readonly busy: boolean;
  readonly testId: string;
  readonly onSubmit: () => void;
  readonly children: ReactNode;
  /** Between the progress and the buttons: the error message. */
  readonly status?: ReactNode;
  readonly actions: ReactNode;
}

/** The common frame of an account flow: title, a fieldset that locks while busy, progress and buttons. */
export function AccountFormFrame({
  title,
  intro,
  busy,
  testId,
  onSubmit,
  children,
  status,
  actions,
}: FormProps) {
  return (
    <form
      className={styles.flow}
      data-testid={testId}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (!busy) onSubmit();
      }}
    >
      <h3 className={styles.flowTitle}>{title}</h3>
      {intro === undefined ? null : <p className={styles.muted}>{intro}</p>}
      <fieldset className={styles.fieldset} disabled={busy}>
        {children}
      </fieldset>
      {busy ? <AccountProgress /> : null}
      {status}
      <div className={styles.actions}>{actions}</div>
    </form>
  );
}
